import {
	festivalClockAfter,
	resolveSafeZonePatch,
} from '#app/domains/program/pure'
import { HomeIcon } from '@radix-ui/react-icons'
import { useState } from 'react'
import {
	data,
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
	redirect,
	Link,
	useActionData,
	useLoaderData,
} from 'react-router'
import { EventForm } from '#app/components/organisms/event'
import {
	festivalDefaultPrices,
	hasNegativePrice,
	parsePriceForm,
} from '#app/domains/pricing'
import { parseSeatsInput, previewSeats } from '#app/domains/tickets/pure'
import { prisma } from '#app/utils/db.server.js'
import {
	ImageUploadError,
	parseMultipartRequest,
	resolveImagePatch,
} from '#app/utils/image.server'
import { requireUserWithPermission } from '#app/utils/permissions.server.ts'
import {
	type EventWithSeatsAndTickets,
	getCurrentFestival,
} from '#app/utils/providers/events.server.js'
import eventCardStyleUrl from '../../styles/event-card.css?url'

// the form previews the program card
export const links = () => [{ rel: 'stylesheet', href: eventCardStyleUrl }]
type ActionData =
	| {
			errors: {
				title?: string
				date?: string
				endTime?: string
				totalSeats?: string
				prices?: string
				server?: string
			}
	  }
	| { event: { id: string; title: string; date: string } }

export const loader = async ({ request }: LoaderFunctionArgs) => {
	try {
		await requireUserWithPermission(request, 'create:event:any')
		const allPerformers = await prisma.performer.findMany({
			orderBy: { name: 'asc' },
			select: { id: true, name: true },
		})
		const festival = await getCurrentFestival()
		return data({
			allPerformers,
			canEdit: true,
			error: null,
			pricingModel: festival?.pricingModel ?? null,
			defaultPrices: festivalDefaultPrices(festival),
		})
	} catch (error) {
		if (error instanceof Response && error.status === 401) {
			throw redirect('/login')
		}
		return data({
			allPerformers: [],
			canEdit: false,
			error: 'Du har ikkje tilgang til å opprette arrangement',
			pricingModel: null,
			defaultPrices: festivalDefaultPrices(null),
		})
	}
}

export const action = async ({ request }: ActionFunctionArgs) => {
	await requireUserWithPermission(request, 'create:event:any')
	const formData = await parseMultipartRequest(request)
	const title = formData.get('title')
	// the browser's own reading of the picked time (the server runs in UTC)
	const date = formData.get('dateWithTimezone') || formData.get('date')
	const description = formData.get('description')
	const shortDescription = formData.get('shortDescription')
	const performers = formData.get('performers')
	const location = formData.get('location')
	const host = formData.get('host')
	const endTime = formData.get('endTime')
	// "berre møt opp": no seats and no prices, whatever the form still holds
	const dropIn = formData.get('dropIn') === 'on'
	const seatsField = dropIn ? null : parseSeatsInput(formData.get('totalSeats'))
	const seatsError =
		seatsField && 'error' in seatsField ? seatsField.error : undefined
	const prices = dropIn ? null : parsePriceForm(formData)

	const negativePrice =
		prices != null && hasNegativePrice(...Object.values(prices))
	// a crafted POST can send anything: only a real instant is a start
	const startsAt =
		typeof date === 'string' && !Number.isNaN(new Date(date).getTime())
			? new Date(date)
			: null
	const endClock = typeof endTime === 'string' ? endTime.trim() : ''
	// the end is a clock time on the start's day (or after midnight)
	const endsAt =
		startsAt && endClock ? festivalClockAfter(startsAt, endClock) : null
	const badEnd = Boolean(startsAt && endClock) && !endsAt

	if (
		typeof title !== 'string' ||
		!title ||
		!startsAt ||
		negativePrice ||
		badEnd ||
		seatsError
	) {
		return data<ActionData>(
			{
				errors: {
					title: !title ? 'Tittel må fyllast ut' : undefined,
					date: !date
						? 'Dato må fyllast ut'
						: !startsAt
							? 'Vel ein gyldig dato og tid'
							: undefined,
					endTime: badEnd
						? 'Vel eit gyldig klokkeslett for slutten'
						: undefined,
					totalSeats: seatsError,
					prices: negativePrice ? 'Prisen kan ikkje vere negativ' : undefined,
				},
			},
			{ status: 400 },
		)
	}

	try {
		const festival = await getCurrentFestival()
		const imageUrlPatch = await resolveImagePatch(formData)
		const selectedPerformerIds = formData
			.getAll('performerIds')
			.filter((id): id is string => typeof id === 'string')
		const event = await prisma.event.create({
			data: {
				title,
				date: startsAt,
				endsAt,
				dropIn,
				festival: festival ? { connect: { id: festival.id } } : undefined,
				imageUrl: imageUrlPatch ?? undefined,
				imageSafeZone: imageUrlPatch
					? resolveSafeZonePatch(formData)
					: undefined,
				featuring: {
					connect: selectedPerformerIds.map((id) => ({ id })),
				},
				description: typeof description === 'string' ? description : undefined,
				shortDescription:
					typeof shortDescription === 'string' && shortDescription.trim() !== ''
						? shortDescription
						: undefined,
				performers: typeof performers === 'string' ? performers : undefined,
				location: typeof location === 'string' ? location : undefined,
				host: typeof host === 'string' ? host : undefined,
				...prices,
				seats:
					seatsField && 'seats' in seatsField
						? { create: { total: seatsField.seats } }
						: undefined,
			},
		})

		return redirect(`/events/${event.id}`)
	} catch (error) {
		if (error instanceof ImageUploadError) {
			return data<ActionData>(
				{ errors: { server: error.message } },
				{ status: 400 },
			)
		}
		console.error('Error creating event:', error)
		return data<ActionData>(
			{ errors: { server: 'Kunne ikkje opprette arrangement' } },
			{ status: 500 },
		)
	}
}

export default function NewEvent() {
	const { allPerformers, canEdit, error, pricingModel, defaultPrices } =
		useLoaderData<typeof loader>()
	const actionData = useActionData<typeof action>()
	const [previewEvent, setPreviewEvent] = useState({
		title: '',
		date: new Date().toISOString(),
		endsAt: '',
		dropIn: false,
		description: '',
		performers: '',
		location: '',
		host: '',
		imageUrl: '',
		imageSafeZone: null,
		// no number yet: the preview card shows no seats, not "Utseld"
		seats: null as { total: number } | null,
		...defaultPrices,
	})

	if (!canEdit) {
		return (
			<main className="font-poppins grid place-items-center p-4 pb-16">
				<div className="text-center">
					<h1 className="mb-4 text-2xl font-bold">Ingen tilgang</h1>
					<p>{error || 'Du har ikkje tilgang til å opprette arrangement'}</p>
				</div>
			</main>
		)
	}

	function handleInputChange(
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) {
		const { name, value } = e.target
		setPreviewEvent((prev) => ({
			...prev,
			[name]:
				name === 'date'
					? new Date(value).toISOString()
					: name === 'dropIn'
						? value === 'true'
						: value,
			seats: name === 'totalSeats' ? previewSeats(value) : prev.seats,
		}))
	}

	return (
		<main className="font-poppins grid place-items-center p-4 pb-16">
			<div className="breadcrumbs w-full max-w-5xl self-start text-sm">
				<ul>
					<li className="animate-roll-reveal">
						<Link to="/">
							<HomeIcon />
						</Link>
					</li>
					<li className="animate-roll-reveal [animation-delay:0.01s]">
						<Link to="/#program">Program</Link>
					</li>
					<li className="animate-roll-reveal font-bold [animation-delay:0.05s]">
						Opprett nytt arrangement
					</li>
				</ul>
			</div>
			<div className="mx-auto my-8 w-full max-w-5xl">
				<header className="mb-8">
					<p className="text-ember-ink text-xs font-bold tracking-[0.14em] uppercase">
						Program
					</p>
					<h1 className="text-h2 mt-1">Nytt arrangement</h1>
				</header>
				<EventForm
					event={previewEvent as unknown as EventWithSeatsAndTickets}
					onInputChange={handleInputChange}
					actionData={actionData}
					allPerformers={allPerformers}
					canAddPerformer={canEdit}
					pricingModel={pricingModel}
				/>
				{actionData && 'errors' in actionData && actionData.errors?.server && (
					<p className="text-error mt-4">{actionData.errors.server}</p>
				)}
				{actionData && 'event' in actionData && (
					<p className="text-success mt-4">Arrangement oppretta!</p>
				)}
			</div>
		</main>
	)
}
